import Color from "color";
import { Choreography, ConvexHull, DanceCouple, DiscreteHeatmap, IntermediatePattern, IntermediatePosition, Pattern, Polyline, Position } from "../interfaces/Choreography";
import { start } from "repl";
import { closest } from "color-2-name";

export function findConvexHull(positions: DanceCouple[], brushSelectedDancers: number[]) {
  if (brushSelectedDancers.length <= 1) {
    return {lineSegments: []};
  }

  const dancerInLeftBottomCorner = findDancerInBottomLeftCorner(positions, brushSelectedDancers);
  const positionOfDancerInLeftBottomCorner = positions[Math.floor((dancerInLeftBottomCorner-1)/2)].position[(dancerInLeftBottomCorner-1)%2];

  const P = { x: positionOfDancerInLeftBottomCorner.x, y: positionOfDancerInLeftBottomCorner.y};

  let otherPoints: { id: number; x: number; y: number; angle: number; distance: number; }[] = [];
  otherPoints.push({id:dancerInLeftBottomCorner,x:positionOfDancerInLeftBottomCorner.x,y:positionOfDancerInLeftBottomCorner.y, angle: -1, distance: 0});

  positions.forEach(danceCouple => {
    const gentlemanId: number = 2*danceCouple.id-1
    const gentlemanX = danceCouple.position[0].x
    const gentlemanY = danceCouple.position[0].y

    if (gentlemanId !== dancerInLeftBottomCorner && brushSelectedDancers.includes(gentlemanId)) {
      otherPoints.push({id: gentlemanId,x: gentlemanX, y: gentlemanY, angle: getAngleWithXAxis(P, {x: gentlemanX, y: gentlemanY}), distance: distanceBetweenPoints(P,{x: gentlemanX, y: gentlemanY})});
    }

    if (!danceCouple.joint) {
      const ladyId: number = 2*danceCouple.id
      const ladyX = danceCouple.position[1].x
      const ladyY = danceCouple.position[1].y
      if (ladyId !== dancerInLeftBottomCorner && brushSelectedDancers.includes(ladyId)) {
        otherPoints.push({id: ladyId,x: ladyX, y: ladyY, angle: getAngleWithXAxis(P, {x: ladyX, y: ladyY}), distance: distanceBetweenPoints(P,{x: ladyX, y: ladyY})});
      }
    }
  })

  const EPS = 0.001;
  otherPoints.sort((a,b) => {
    const angleDifference = a.angle-b.angle;
    const distanceDifference = a.distance - b.distance;

    if (angleDifference > EPS) {
      return 1;
    }else if (angleDifference < EPS) {
      return -1;
    }else {
      if (distanceDifference > EPS) {
        return 1;
      }else if (distanceDifference < EPS) {
        return -1
      }else {
        return 0;
      }
    }
  })


  let idsToRemove:number[] = [];
  otherPoints.forEach((controlPoint) => {
    otherPoints.forEach(referencePoint => {
      if (controlPoint !== referencePoint && controlPoint.id !== dancerInLeftBottomCorner ) {
        const a = Math.abs(controlPoint.angle-referencePoint.angle)
        if (a < EPS && controlPoint.distance < referencePoint.distance) {
          if (!idsToRemove.includes(controlPoint.id)) {
            idsToRemove.push(controlPoint.id);
          }
        }
      }
    })
  })

  otherPoints = otherPoints.filter(item => !idsToRemove.includes(item.id))
  otherPoints.push({id:dancerInLeftBottomCorner,x:positionOfDancerInLeftBottomCorner.x,y:positionOfDancerInLeftBottomCorner.y, angle: -1, distance: 0});


  let stack = [];
  for (let i = 0; i < otherPoints.length; i++) {
    let point = otherPoints[i];

    while (stack.length > 1 && ccw(stack[stack.length - 2], stack[stack.length - 1], point)<=0) {
      stack.pop();
    }
    stack.push(point);
  }


  let convexHullLines: Polyline = {id: -1, lineSegments: []};
  for (let i=0; i<stack.length-1; i++) {
    convexHullLines.lineSegments.push({sourceId: stack[i].id, targetId: stack[i+1].id});
  }
  let convexHull: ConvexHull = {id: -1, polyline: convexHullLines, brushSelectedDancers: brushSelectedDancers};


  return convexHull;
}

function ccw (P1: any, P2: any, P3 : any) {
  return (P2.x-P1.x)*(P3.y-P1.y) - (P2.y-P1.y)*(P3.x-P1.x)
}

function getAngleWithXAxis(P:{x: number, y: number}, Q:{x: number, y: number}) {
  return Math.acos((Q.x-P.x)/vectorLength({x:Q.x-P.x, y:Q.y-P.y}));
}

function vectorLength(vector: {x:number, y:number}) {
  return Math.sqrt(Math.pow(vector.x,2) + Math.pow(vector.y,2))
}

function distanceBetweenPoints(P:{x:number, y:number}, Q:{x:number, y:number}) {
  return Math.sqrt(Math.pow(P.x-Q.x,2)+Math.pow(P.y-Q.y,2));
}

function findDancerInBottomLeftCorner(positions: DanceCouple[], brushSelectedDancers: number[]) {
  let minimalX: number = Number.MAX_VALUE;
  let minimalY: number = Number.MAX_VALUE;
  let dancerInBottomLeftCorner: number = -1;
  positions.forEach((danceCouple) => {
    let i = 0;
    danceCouple.position.forEach((position) => {
      if (brushSelectedDancers.includes(2*danceCouple.id+i-1)) {
        if (position.y < minimalY || (position.y === minimalY && position.x < minimalX)) {
          minimalX = position.x;
          minimalY = position.y;
          dancerInBottomLeftCorner = 2*danceCouple.id+i-1;
        }
      }
      i++;
    })
  })
  return dancerInBottomLeftCorner;
}


export function getPositionAfterPatterRotation(position: Position, patternRotation: "Front"|"Back"|"Left"|"Right") {
  let resultPosition: Position = {x: position.x, y: position.y, bodyOrientation: position.bodyOrientation, headOrientation: position.headOrientation, pose: position.pose, tangentBodyPart: "None"};
  calcCoordinatesAfterRotation(position, resultPosition, patternRotation);
  return resultPosition;
}

export function getIntermediatePositionAfterPatterRotation(position: IntermediatePosition, patternRotation: "Front"|"Back"|"Left"|"Right") {
  let resultPosition: IntermediatePosition = {x: position.x, y: position.y};
  calcCoordinatesAfterRotation(position, resultPosition, patternRotation);
  return resultPosition;
}


function calcCoordinatesAfterRotation(position: Position|IntermediatePosition, resultPosition: Position|IntermediatePosition,  patternRotation: "Front"|"Back"|"Left"|"Right") {
  switch(patternRotation) {
    case "Left":
      resultPosition.x = position.y;
      resultPosition.y = -position.x; 
      break;
    case "Back":
      resultPosition.y *= -1;
      resultPosition.x *= -1;
      break;
    case "Right":
      resultPosition.x = -position.y;
      resultPosition.y = position.x; 
      break;
  }
}

export function getXAfterRotationInverse(x: number, y: number, patternRotation: "Front"|"Back"|"Left"|"Right") {
  switch(patternRotation) {
    case "Left":
      return -y;
    case "Back":
      return x *= -1;
    case "Right":
      return y;
    case "Front":
      return x;
  }
}

export function getYAfterRotationInverse(x: number, y: number, patternRotation: "Front"|"Back"|"Left"|"Right") {
  switch(patternRotation) {
    case "Left":
      return x;
    case "Back":
      return y *= -1;
    case "Right":
      return -x;
    case "Front":
      return y;
  }
}


export function getXAfterRotation(x: number, y: number, patternRotation: "Front"|"Back"|"Left"|"Right") {
  switch(patternRotation) {
    case "Left":
      return y;
    case "Back":
      return x *= -1;
    case "Right":
      return -y;
    case "Front":
      return x;
  }
}

export function getYAfterRotation(x: number, y: number, patternRotation: "Front"|"Back"|"Left"|"Right") {
  switch(patternRotation) {
    case "Left":
      return -x;
    case "Back":
      return y *= -1;
    case "Right":
      return x;
    case "Front":
      return y;
  }
}




export function getOrientationAfterRotation(rotation: number, patternRotation : "Front"|"Back"|"Left"|"Right") {
  let resultRotation = rotation
  switch(patternRotation) {
    case "Left":
      resultRotation = (rotation + 90) % 360;
      break;
    case "Back":
      resultRotation = (rotation + 180) % 360;
      break;
    case "Right":
      resultRotation = (rotation + 270) % 360;
      break;
  }
  return resultRotation;
}

export function calcSimpleCoordinatesAfterRotation(
  position: { x: number; y: number },
  patternRotation: "Front" | "Back" | "Left" | "Right"
) {
  let resultPosition = { x: position.x, y: position.y };
  switch (patternRotation) {
    case "Left":
      resultPosition.x = position.y;
      resultPosition.y = -position.x;
      break;
    case "Back":
      resultPosition.y *= -1;
      resultPosition.x *= -1;
      break;
    case "Right":
      resultPosition.x = -position.y;
      resultPosition.y = position.x;
      break;
  }
  return resultPosition;
}

export function isHighlighted(
    value: number,
    selectedDancer: number,
    positions: DanceCouple[],
    axisAlignment: "horizontal" | "vertical",
    draggedDancerPosition: {x: number, y: number},
    patternRotation: "Front"|"Back"|"Left"|"Right"
  ): boolean {

    if (axisAlignment === "horizontal" && draggedDancerPosition.y === value) {
      return true;
    }
    if (axisAlignment === "vertical" && draggedDancerPosition.x === value) {
      return true;
    }

    if (selectedDancer > -1) {
      const coupleNumber: number = Math.floor((selectedDancer - 1) / 2);
      const dancerNumberInDanceCouple: number = (selectedDancer - 1) % 2;
  
      if (axisAlignment === "horizontal") {
        if (
          calcSimpleCoordinatesAfterRotation(positions[coupleNumber].position[dancerNumberInDanceCouple],patternRotation).x === value
        ) {
          return true;
        }
      } else {
        if (
          calcSimpleCoordinatesAfterRotation(positions[coupleNumber].position[dancerNumberInDanceCouple],patternRotation).y === value
        ) {
          return true;
        }
      }
    }
  
    return false;
}

export function getBeatDistance(patternBefore: Pattern|IntermediatePattern, patternAfter: Pattern|IntermediatePattern) {
  let beatDistance: number = patternAfter.beat - patternBefore.beat;
        if (patternAfter.bar !== patternBefore.bar) {
          beatDistance =
            patternAfter.beat +
            1 +
            (7 - patternBefore.beat) +
            (patternAfter.bar - patternBefore.bar - 1) * 8;
        }
  return beatDistance;
}


export function isInteger(value: number): boolean {
  if (value - Math.floor(value) === 0) {
    return true;
  }
  return false;
}

export function getBrushX(mouseMovePosition: {x:number, y:number}, mouseDownPosition: {x:number, y:number}) {
  if (mouseMovePosition.x < mouseDownPosition.x) {
    return mouseMovePosition.x;
  }
  return mouseDownPosition.x;
}

export function getBrushY(mouseMovePosition: {x:number, y:number}, mouseDownPosition: {x:number, y:number}): number {
  if (mouseMovePosition.y < mouseDownPosition.y) {
    return mouseMovePosition.y;
  }
  return mouseDownPosition.y;
}

export function getBrushWidth(mouseMovePosition: {x:number, y:number}, mouseDownPosition: {x:number, y:number}) {
  if (mouseMovePosition.x < 0) {
    return 0;
  }

  if (mouseMovePosition.x < mouseDownPosition.x) {
    return mouseDownPosition.x - mouseMovePosition.x;
  }

  return Math.abs(mouseMovePosition.x - mouseDownPosition.x);
}

export function getBrushHeight(mouseMovePosition: {x:number, y:number}, mouseDownPosition: {x:number, y:number}) {
  if (mouseMovePosition.y < 0) {
    return 0;
  }

  if (mouseMovePosition.y < mouseDownPosition.y) {
    return mouseDownPosition.y - mouseMovePosition.y;
  }

  return Math.abs(mouseMovePosition.y - mouseDownPosition.y);
}

export function getBoundingClientRect (className: string) {
  return document
  .getElementsByClassName(className)[0]
  .getBoundingClientRect();
}


export function clampToGridPosition(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;

  const gridResolution = 0.5;
  const distanceToLeftInteger = Math.abs(value - Math.floor(value));
  const distanceToRightInteger = Math.abs(Math.ceil(value) - value);
  const midValue = Math.floor(value) + gridResolution;
  const distanceToMidValue = Math.abs(value-midValue);
  const minimalDistance = Math.min(distanceToLeftInteger, distanceToRightInteger, distanceToMidValue)

  if (distanceToLeftInteger === minimalDistance) {
    return Math.floor(value);
  }else if (distanceToRightInteger === minimalDistance) {
    return Math.ceil(value);
  }
  return Math.floor(value)+gridResolution;
}


export function getGaussian2DKernel(kernelPixelWidth: number) {
  const kernelExtent = Math.floor(kernelPixelWidth / 2);
  let kernel: number[][] = [];
  for (let row=0; row<kernelPixelWidth; row++) {
    kernel.push([]);
    for (let column=0; column<kernelPixelWidth; column++) {
      const value = gauss2D(column-kernelExtent, row-kernelExtent, kernelPixelWidth);
      kernel[row].push(value);
    }
  }
  return kernel;
}

function gauss2D(x: number, y: number, kernelPixelWidth: number) {
  const sigma = kernelPixelWidth/5;
  return 1/(2*Math.PI*sigma*sigma)*Math.exp(-(x*x+y*y)/(2*sigma*sigma));
}

export function findMaxFrequencyInHeatmap(heatmap: DiscreteHeatmap) {
  const heatmapData = heatmap.values;
  let maxFrequency = Number.MIN_VALUE;
  for (let i = 0; i < heatmapData.length; i++) {
    for (let j = 0; j < heatmapData[i].length; j++) {
      if (heatmapData[i][j] > maxFrequency) {
        maxFrequency = heatmapData[i][j];
      }
    }
  }
  return maxFrequency;
}

export function allPairsJoint(danceCouples: DanceCouple[]) {
  let allPairsJoint = true;
  danceCouples.forEach(danceCouple => {
    if (!danceCouple.joint) {
      allPairsJoint = false;
    }
  })
  return allPairsJoint;
}

export function allPairsSeparated(danceCouples: DanceCouple[]) {
  let allPairsSeparated = true;
  danceCouples.forEach(danceCouple => {
    if (danceCouple.joint) {
      allPairsSeparated = false;
    }
  })
  return allPairsSeparated;
}


export function isPointSymmetric(positions: DanceCouple[], brush: number[], centerPos: [number, number]): boolean {
  let isSymmetric = true;
  brush.forEach(id=> {

    const dancerPos = positions[Math.floor((id - 1) / 2)].position[(id - 1) % 2];
    const centerToDancer: [number, number] = [dancerPos.x-centerPos[0],dancerPos.y-centerPos[1]];
    const vectorToCheck: [number, number] = [-centerToDancer[0], -centerToDancer[1]];

    let found = false;
    brush.forEach(idToCheck => {
      const dancerPosToCheck = positions[Math.floor((idToCheck - 1) / 2)].position[(idToCheck - 1) % 2];
      if (centerPos[0]+vectorToCheck[0] === dancerPosToCheck.x && centerPos[1]+vectorToCheck[1] === dancerPosToCheck.y) {
        found = true;
      }
    });
    if (!found) {
      isSymmetric = false;
      return;
    };
  })


  return isSymmetric;
}

/**
 * Returns a lighter version of the input color
 *
 * @param color Input color string (Color types need to be converted first)
 * @param colorLightenFactor Factor with which to lighten the color (from 0 to 1). The
 * higher the number, the lighter the color will get.
 * @returns The lighter color as a string (no conversion is needed if the color string
 * needs to be assigned as a fill/stroke/etc. attribute)
 */
export function lightColor(color: string, colorLightenFactor: number): string {
  return Color(color).lighten(colorLightenFactor).hex();
}


/**
 * Calculates the position of the partner in a couple, that depends on the position of the corresponding dominant partner and his body orientation.
 * The displacement distance is currently 40 units in 3D space. Dominant partner means that he faces in the direction that the couple orientation dictates.
 * This is the case, if "Gentleman ...", "Lady ..." is selected as tangent body part. If tangent body part is "Couple center" the man will get the couples orientation.
 * 
 * @param gentlemanPosition Position/Orientation of the dominant partner in 3D coordinates.
 * @returns Returns the ladies position in 3D coordinates
 */
export function displacePartner(pos: [number, number, number], bodyOrientation: number, displacementFactor: number) {
  const directionVectors: [number,number][] = [[0,1],[0.7,0.7],[1,0],[0.7,-0.7],[0,-1],[-0.7,-0.7],[-1,0],[-0.7,0.7]];

  const index: number = Math.floor(bodyOrientation / 45);
  const displacementDir: [number, number] = directionVectors[index];

  pos[0] += displacementDir[0] * 100 * displacementFactor * 0.4;
  pos[2] += displacementDir[1] * -100 * displacementFactor * 0.4;


  return pos;
}

export function tangentBodyPartNameToNumber(name: string, coupleJoint: boolean) {
  let newTangentBodyPart: number = 0;
  if (!coupleJoint) {
    switch(name) {
      case "Body Center": newTangentBodyPart = 0; break;
      case "Left Foot": newTangentBodyPart = 1; break;
      case "Right Foot": newTangentBodyPart = 2; break;
    }
  }else {
    switch(name) {
      case "Couple Center": newTangentBodyPart = 0; break;
      case "Gentleman Body Center": newTangentBodyPart = 1; break;
      case "Gentleman Left Foot": newTangentBodyPart = 2; break;
      case "Gentleman Right Foot": newTangentBodyPart = 3; break;
      case "Lady Body Center": newTangentBodyPart = 4; break;
      case "Lady Left Foot": newTangentBodyPart = 5; break;
      case "Lady Right Foot": newTangentBodyPart = 6; break;
    }
  }

  return newTangentBodyPart;
}

export function tangentBodyPartNumberToName(id: number, coupleJoint: boolean) {
  let newTangentBodyPart: string = "None";
  if (!coupleJoint) {
    switch(id) {
      case 0: newTangentBodyPart="Body Center"; break;
      case 1: newTangentBodyPart="Left Foot"; break;
      case 2: newTangentBodyPart="Right Foot"; break;
    }
  }else {
    switch(id) {
      case 0: newTangentBodyPart="Couple Center"; break;
      case 1: newTangentBodyPart="Gentleman Body Center"; break;
      case 2: newTangentBodyPart="Gentleman Left Foot"; break;
      case 3: newTangentBodyPart="Gentleman Right Foot"; break;
      case 4: newTangentBodyPart="Lady Body Center"; break;
      case 5: newTangentBodyPart="Lady Left Foot"; break;
      case 6: newTangentBodyPart="Lady Right Foot"; break;
    }
  }

  return newTangentBodyPart;
}


function displaceInDirection(pos: [number, number, number], bodyOrientation: number, direction: "Left" | "Right") {
  const directionVectors: [number,number][] = [[1,0],[0.7,-0.7],[0,-1],[-0.7,-0.7],[-1,0],[-0.7,0.7],[0,1],[-0.7,0.7]];
  const index: number = Math.floor(bodyOrientation / 45);

  const displacementFactor: number = 0.1;
  const displacementDir: [number, number] = directionVectors[index];


  switch(direction) {
    case "Left":
      pos[0] += displacementFactor * displacementDir[0] * 100;
      pos[2] -= displacementFactor * displacementDir[1] * 100;
      break;
    case "Right":
      pos[0] -= displacementFactor * displacementDir[0] * 100;
      pos[2] += displacementFactor * displacementDir[1] * 100;
      break;
  }

  return pos;
}


export function calculateSeparatedPosition(dancer: Position) {
  let pos: [number, number, number] = [dancer.x * 100,-100,-dancer.y * 100];

  switch(dancer.tangentBodyPart) {
    case "Left Foot":
      pos = displaceInDirection(pos, dancer.bodyOrientation, "Left");
      break;
    case "Right Foot":
      pos = displaceInDirection(pos, dancer.bodyOrientation, "Right");
      break;
  }

  return pos;
}

export function calculateCouplePositionGentleman(couple: DanceCouple) {
  let pos: [number, number, number] = [couple.position[0].x * 100,-100,-couple.position[0].y * 100];
  const gentleman = couple.position[0];

  const manDominantConstellations = ["Gentleman Left Foot", "Gentleman Right Foot", "Gentleman Body Center"];
  const ladyDominantConstellation = ["Lady Left Foot", "Lady Right Foot", "Lady Body Center"];

  if (manDominantConstellations.includes(gentleman.tangentBodyPart)) {
    switch(gentleman.tangentBodyPart) {
      case "Gentleman Left Foot":
        pos = displaceInDirection(pos, gentleman.bodyOrientation, "Left");
        break;
      case "Gentleman Right Foot":
        pos = displaceInDirection(pos, gentleman.bodyOrientation, "Right");
    }
  }else if (ladyDominantConstellation.includes(gentleman.tangentBodyPart)) {
    let partnerPos: [number, number, number] = [couple.position[0].x * 100,-100,-couple.position[0].y * 100]; 
    switch(gentleman.tangentBodyPart) {
      case "Lady Left Foot":
        partnerPos = displaceInDirection(pos, gentleman.bodyOrientation, "Left");
        break;
      case "Lady Right Foot":
        partnerPos = displaceInDirection(pos, gentleman.bodyOrientation, "Right");
    }
    pos = displacePartner(partnerPos, gentleman.bodyOrientation, 1.0);
  }else {
    pos = displacePartner(pos, gentleman.bodyOrientation, -0.5);
  }

  return pos;
}

export function calculateCouplePositionLady(couple: DanceCouple) {
  let pos: [number, number, number] = [couple.position[1].x * 100,-100,-couple.position[1].y * 100];
  const lady = couple.position[0];

  const manDominantConstellations = ["Gentleman Left Foot", "Gentleman Right Foot", "Gentleman Body Center"];
  const ladyDominantConstellation = ["Lady Left Foot", "Lady Right Foot", "Lady Body Center"];

  if (ladyDominantConstellation.includes(lady.tangentBodyPart)) {
    switch(lady.tangentBodyPart) {
      case "Lady Left Foot":
        pos = displaceInDirection(pos, lady.bodyOrientation, "Left");
        break;
      case "Lady Right Foot":
        pos = displaceInDirection(pos, lady.bodyOrientation, "Right");
    }
  }else if (manDominantConstellations.includes(lady.tangentBodyPart)) {
    let partnerPos: [number, number, number] = [couple.position[1].x * 100,-100,-couple.position[1].y * 100]; 
    switch(lady.tangentBodyPart) {
      case "Gentleman Left Foot":
        partnerPos = displaceInDirection(pos, lady.bodyOrientation, "Left");
        break;
      case "Gentleman Right Foot":
        partnerPos = displaceInDirection(pos, lady.bodyOrientation, "Right");
    }
    pos = displacePartner(partnerPos, lady.bodyOrientation, 1.0);
  }else {
    pos = displacePartner(pos, lady.bodyOrientation, 0.5);
  }

  return pos;
}

export function calculateSeparatedRotation(dancer: Position) {
  return -((dancer.bodyOrientation - 180) * Math.PI) / 180;
}

export function calculateCoupleRotationGentleman(couple: DanceCouple) {
  const gentleman = couple.position[0];
  const lady = couple.position[1];

  if (Math.abs(gentleman.bodyOrientation - lady.bodyOrientation) !== 180) {
    return -((gentleman.bodyOrientation - 180) * Math.PI) / 180;
  }

  const manDominantConstellations = ["Gentleman Left Foot", "Gentleman Right Foot", "Gentleman Body Center"];
  const ladyDominantConstellation = ["Lady Left Foot", "Lady Right Foot", "Lady Body Center"];


  if (manDominantConstellations.includes(gentleman.tangentBodyPart)) {
    return -((gentleman.bodyOrientation - 180) * Math.PI) / 180;
  }else if (ladyDominantConstellation.includes(gentleman.tangentBodyPart)) {
    return -((gentleman.bodyOrientation) * Math.PI) / 180;
  }else {
    return -((gentleman.bodyOrientation - 180) * Math.PI) / 180;
  }
}

export function calculateCoupleRotationLady(couple: DanceCouple) {
  const gentleman = couple.position[0];
  const lady = couple.position[1];
  
  if (Math.abs(gentleman.bodyOrientation - lady.bodyOrientation) !== 180) {
    return -((lady.bodyOrientation - 180) * Math.PI) / 180;
  }


  const manDominantConstellations = ["Gentleman Left Foot", "Gentleman Right Foot", "Gentleman Body Center"];
  const ladyDominantConstellation = ["Lady Left Foot", "Lady Right Foot", "Lady Body Center"];

  if (ladyDominantConstellation.includes(lady.tangentBodyPart)) {
    return -((lady.bodyOrientation - 180) * Math.PI) / 180;
  }else if (manDominantConstellations.includes(lady.tangentBodyPart)) {
    return -((lady.bodyOrientation) * Math.PI) / 180;
  }else {
    return -((lady.bodyOrientation - 180) * Math.PI) / 180;
  }
}


export function getStartBarForDanceRemoval(choreo: Choreography, danceId: number) {
  let startInBars: number = 0;

  for(let i=0;i<choreo.dances.length;i++) {
    const dance = choreo.dances[i];
    if (i < danceId) {
        startInBars += dance.bars;
    }
  }

  return startInBars;
}

export function getEndBarForDanceRemoval(choreo: Choreography, danceId: number) {
  let startInBars: number = 0;
  let endInBars: number = 0;

  for(let i=0;i<choreo.dances.length;i++) {
    const dance = choreo.dances[i];
    if (i < danceId) {
        startInBars += dance.bars;
    }

    if (i === danceId) {
      endInBars = startInBars + dance.bars-1;
      break;
    }
  }

  return endInBars;
}


export function getFirstPatternId(choreo: Choreography, startInBars: number, endInBars: number) {
  let firstPatternId: number = -1;

  choreo.patterns.forEach(pattern => {
    if (pattern.bar >= startInBars && pattern.bar <= endInBars) {

      if (firstPatternId == -1) {
        firstPatternId = pattern.id;
      }
    }
  });

  return firstPatternId;
}

export function getPatternsInDanceAmount(choreo: Choreography, startInBars: number, endInBars: number) {
  let patternsInDanceAmount: number = 0;

  choreo.patterns.forEach(pattern => {
    if (pattern.bar >= startInBars && pattern.bar <= endInBars) {
      patternsInDanceAmount++;
    }

  });

  return patternsInDanceAmount;
}

/**
 * Custom color luminance approximation that assigns the same weight to each color component
 */
function lum(color: Color) {
  return (0.33*color.red() + 0.33*color.green() + 0.33*color.blue())/256.0;
}

/**
 * Quadratic formula that converts the difference between color and background into a suitable
 * color lighten factor.
 * Tries to prevent that very light colors get white.
 */
export function diffToLighten(diff:number) {
  return diff*diff+0.1;
}

/**
 * Luminance difference between two colors that uses the self-defined
 * luminance approximation
 */
export function lumDiffAlt(color1: Color, color2: Color) {
  return Math.abs(lum(color1)-lum(color2));
}

/**
 * Lightens the given color by the given factor.
 * If the color is white, the input color will be returned.
 */
export function neighborLighten(color: Color, colorLightenFactor: number) {
  const hsv = color.hsv();
  if (hsv.value()!==100 || hsv.saturationv()!==0) {
     return customLighten(color, colorLightenFactor);
  }
  return color;
}

/**
 * Lightens the given color by the given factor.
 * If the color is white, return a light gray.
 * If the color is black, return a dark gray.
 */
export function customLighten(color: Color, colorLightenFactor: number) {
  const hsv = color.hsv();
  if (hsv.value()===100 && hsv.saturationv()===0) {
    return Color("#DDDDDD"); 
  }
  if (hsv.value() === 0) {
    return Color("#555555");
  }
  return color.lighten(colorLightenFactor);
}

/**
 * Returns the luminance difference between 2 colors (value in [0,1])
 */
export function lumDiff(color1:Color, color2:Color) {
  return Math.abs(color1.luminosity()-color2.luminosity());
}

/**
 * Checks the luminance difference between the given color and black/white.
 * Returns black or white, depending on which "color" yields the bigger luminance difference to
 * the given color.
 */
export function getFontColor(color:string): string {
  const diffToBlack = lumDiff(Color(color), Color("#000000"));
  const diffToWhite = lumDiff(Color(color), Color("#FFFFFF"));

  if (diffToBlack > diffToWhite) {
    return "#000000";
  }else {
    return "#FFFFFF";
  }
}

/**
 * Returns black, if the luminance difference between the background and the given fill color
 * and returns black, if that value is below a certain threshold. Otherwise return the input color.
 */
export function getStrokeFromColor(color:string, threshold: number): string {
  if (lumDiff(Color(color), Color("#FFFFFF")) < threshold) {
    return "#000000";
  }else {
    return color;
  }
}

/**
 * Automatically calculates the closest name of the given color (with an uppercase letter at the start)
 */
export function formattedColorName(color: string) {
  let unformattedName = closest(color).name;
  return unformattedName.charAt(0).toUpperCase() + unformattedName.slice(1);
}